import { useQuery } from '@tanstack/react-query'
import Cookies from 'js-cookie'

import { ConditionsReportResponse } from '@nx/api'
import { createAuthenticatedAxiosClient } from '@nx/axios-client'
import { populateRelativeUrl } from '@nx/populate-url'

export function useGetConditionsReportRetry(
  lotId: number,
  baseURL: string,
  conditionsReportParams?: ConditionsReportResponse
) {
  const cookies = Cookies.get()

  const authenticatedAxiosClient = createAuthenticatedAxiosClient({
    accessTokenSSR: cookies?.bonhams_access || '',
    refreshTokenSSR: cookies?.bonhams_token || '',
    baseURL,
  })

  let url = `/api/auctions/condition-report/${lotId}/redirect/`

  if (conditionsReportParams?.redirect) {
    const { redirect } = conditionsReportParams
    url = populateRelativeUrl(url, redirect)
  }

  const { data, status, fetchStatus, error, refetch } = useQuery({
    queryKey: ['conditionsReportRetry', url],
    queryFn: async () => {
      try {
        const response = await authenticatedAxiosClient.get(url, {
          headers: { Accept: 'application/pdf, text/plain, */*' },
        })

        const responseJson = response.data

        if (responseJson?.redirect?.includes('saction=Show')) {
          return responseJson
        } else {
          throw new Error('Conditions report not ready, try again')
        }
      } catch (error) {
        if (error instanceof Error) {
          throw new Error((error as Error).message)
        } else {
          throw new Error('Failed to get conditions report')
        }
      }
    },
    retry: 30,
    retryDelay: 1_000,
    enabled: conditionsReportParams?.status === 'REDIRECT',
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchIntervalInBackground: false,
    refetchOnReconnect: false,
    retryOnMount: false,
  })

  return {
    data,
    status,
    fetchStatus,
    error,
    refetch,
  }
}
